import { useEffect, useState } from "react";
import { ShieldCheck, CircleAlert } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { fetchPublicSettings } from "@/lib/api";

export default function PrivacyPage() {
  const [sections, setSections] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPublicSettings()
      .then((s) => setSections(Array.isArray(s.privacy_sections) ? s.privacy_sections : []))
      .catch(() => setSections([]));
  }, []);

  const pendingValidation = (sections || []).some((s) => s.needs_validation);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-[720px] w-full mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 text-[#235277]">
          <ShieldCheck className="w-5 h-5" aria-hidden="true" />
          <span className="text-xs font-semibold tracking-[0.15em] uppercase">Confidentialité</span>
        </div>
        <h1 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-[#060606]">
          Politique de confidentialité
        </h1>

        {pendingValidation && (
          <p
            className="mt-4 text-xs leading-relaxed font-medium text-[#5E5515] bg-[#FDF6E7] border border-[rgba(94,85,21,0.25)] rounded-lg px-3 py-2"
            data-testid="privacy-validation-notice"
          >
            Certaines sections de cette page sont en attente d’informations définitives et d’une
            validation professionnelle avant le lancement public. Elles sont signalées ci dessous.
          </p>
        )}

        <div className="mt-8 space-y-6" data-testid="privacy-content">
          {sections === null ? (
            <>
              <Skeleton className="h-5 w-full rounded" />
              <Skeleton className="h-5 w-5/6 rounded" />
              <Skeleton className="h-5 w-4/6 rounded" />
            </>
          ) : sections.length > 0 ? (
            sections.map((section) => (
              <section
                key={section.key}
                className="rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-5 sm:p-6 shadow-[var(--shadow-sm)]"
                data-testid={`privacy-section-${section.key}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <h2 className="font-heading text-base font-semibold text-[#060606]">{section.title}</h2>
                  {section.needs_validation && (
                    <Badge
                      className="shrink-0 gap-1 bg-[#FDF6E7] text-[#5E5515] border border-[rgba(94,85,21,0.25)] hover:bg-[#FDF6E7] font-medium"
                      data-testid={`privacy-section-${section.key}-validation-badge`}
                    >
                      <CircleAlert className="w-3 h-3" aria-hidden="true" />
                      Validation professionnelle requise
                    </Badge>
                  )}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[#333333] whitespace-pre-line">{section.content}</p>
              </section>
            ))
          ) : (
            <p className="text-sm text-[#333333]">
              Le contenu de la politique de confidentialité sera publié ici.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
